import { ChevronDown, ChevronRight, ExternalLink, Plus } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type {
  ChannelRecord,
  RoutingSkuTargetRecord,
  SkuSupplyCandidate,
} from '@nadoutong/contracts';
import { OpsApiError } from '../../api/client.ts';
import { useSkuSupplyCandidates } from '../../api/skuSupplyCandidates.ts';
import { useRoutingDefaultSkuTargets } from '../../api/useTenantRouting.ts';
import { useT } from '../../i18n/index.ts';
import { findRowMatchedByUpsert, sortSkuPoolTargets } from '../../pages/tenantRoutingShared.ts';
import type { SkuOption } from '../../pages/tenantRoutingShared.ts';
import { TenantRoutingTargetDialog } from '../../pages/TenantRoutingTargetDialog.tsx';
import type { SkuTargetDialogSubmission } from '../../pages/TenantRoutingTargetDialog.tsx';
import {
  Button,
  Chip,
  ConfirmDialog,
  InfoHint,
  Panel,
  Skeleton,
  Table,
  TBody,
  Td,
  Tr,
} from '../../ui/index.ts';
import { useToast, coerceErrorMessage } from '../Toast.tsx';
import { BoundSupplyRow } from './BoundSupplyRow.tsx';
import { CandidateSupplyRow } from './CandidateSupplyRow.tsx';
import { ExclusiveCoverageAlert } from './ExclusiveCoverageAlert.tsx';
import { PinCapabilityDialog } from './PinCapabilityDialog.tsx';
import { SupplyPolicyFooter } from './SupplyPolicyFooter.tsx';
import { SUPPLY_COLUMN_COUNT, SupplyTableHead } from './SupplyTableHead.tsx';
import { groupSupplyRows } from './supplyRows.ts';
import type { BoundSupplyRow as BoundRowModel } from './supplyRows.ts';
import { useSupplyPanelWrites } from './useSupplyPanelWrites.ts';

/**
 * Supply — the SKU detail page's single write hub for "which upstream supply
 * serves this SKU" (#1428, merging the #1327 PR-4 binding panel with the
 * #909 default-pool editor).
 *
 * The two predecessors read different endpoints but wrote the SAME table
 * through the SAME hook, which made the page contradict itself: a target the
 * pool listed could be missing from the candidate list, and the operator had
 * no way to tell whether that meant "not bound" or "bound, just invisible
 * here". One panel, one row set, three groups.
 *
 * Row membership lives in `supplyRows.ts` and identity discipline in
 * `useSupplyPanelWrites.ts`; this component owns layout and dialog state.
 */

function GroupHeaderRow({
  children,
  testId,
}: {
  readonly children: ReactNode;
  readonly testId: string;
}): JSX.Element {
  return (
    <Tr interactive={false}>
      <Td colSpan={SUPPLY_COLUMN_COUNT} className="bg-well/60 py-2" data-testid={testId}>
        {children}
      </Td>
    </Tr>
  );
}

function GroupNoticeRow({
  children,
  testId,
}: {
  readonly children: ReactNode;
  readonly testId: string;
}): JSX.Element {
  return (
    <Tr interactive={false}>
      <Td colSpan={SUPPLY_COLUMN_COUNT} className="text-paper-low" data-testid={testId}>
        {children}
      </Td>
    </Tr>
  );
}

export function SupplyPanel({
  sku,
  namespaceChannel,
}: {
  readonly sku: SkuOption;
  readonly namespaceChannel: ChannelRecord;
}): JSX.Element {
  const t = useT();
  const toast = useToast();
  const targetsQ = useRoutingDefaultSkuTargets(sku.skuId);
  const candidatesQ = useSkuSupplyCandidates(sku.skuId);
  const writes = useSupplyPanelWrites(sku);

  const [addOpen, setAddOpen] = useState(false);
  const [pinTarget, setPinTarget] = useState<RoutingSkuTargetRecord | null>(null);
  /**
   * The capability the operator clicked "bind" on before being diverted into
   * the pin flow. Pre-selecting it is safe: it was in an UNBOUND group, so no
   * target matched it, so it cannot be in `capabilityIdsInUse` — and it saves
   * hunting for the row they just picked in a list keyed by capability id.
   */
  const [pinSeedCapabilityId, setPinSeedCapabilityId] = useState<string | null>(null);
  const [deleteRow, setDeleteRow] = useState<BoundRowModel | null>(null);
  const [crossModelOpen, setCrossModelOpen] = useState(false);

  const orderedTargets = useMemo(
    () => sortSkuPoolTargets(targetsQ.data?.targets ?? []),
    [targetsQ.data],
  );
  /*
    `isSuccess`, not "has data", on BOTH reads — a deliberate trade-off.

    React Query keeps the last good payload when a background refetch fails,
    so `data !== undefined` would let the panel keep asserting off a list it
    can no longer confirm. `isSuccess` instead makes the candidate groups
    disappear for the duration of a failed refetch — including the one every
    bind triggers through `invalidateSkuTargetQueries`.

    That flicker is the price and it is the right way round: everything these
    two flags gate is an ASSERTION about routing state ("nothing is bound",
    "no same-model supply exists", "this pin is not in the candidate list") or
    a WRITE whose safety check reads the list. Showing less while unsure costs
    a refresh; asserting from an unconfirmed list costs a wrong bind.
  */
  const candidatesLoaded = candidatesQ.isSuccess;
  const targetsLoaded = targetsQ.isSuccess;
  const groups = useMemo(
    () => groupSupplyRows({
      targets: orderedTargets,
      candidates: candidatesQ.data?.candidates ?? [],
      candidatesLoaded,
      skuModel: sku.model,
    }),
    [orderedTargets, candidatesQ.data, candidatesLoaded, sku.model],
  );
  const impacts = targetsQ.data?.exclusive_assignment_impact?.affected_active_configs ?? [];
  // The repair copies a target into the exclusive configs, and the copy must
  // carry a capability pin — an unpinned source disables the fix button. So
  // pick the first PINNED row rather than simply the first row: with a legacy
  // row sorted to the top, the repair would otherwise look impossible even
  // though a pinned sibling sits right below it.
  const exclusiveCopySource = orderedTargets.find((target) => target.capability_id !== null)
    ?? orderedTargets[0];
  /** Both reads landed and neither produced a single row to show or offer. */
  const nothingAvailable = targetsLoaded &&
    candidatesLoaded &&
    groups.bound.length === 0 &&
    groups.recommended.length === 0 &&
    groups.crossModel.length === 0;
  const emptyPool = targetsQ.data !== undefined &&
    !targetsQ.data.targets.some((target) => target.enabled);
  const capabilityIdsInUse = useMemo(
    () => new Set(orderedTargets.flatMap((target) =>
      target.capability_id === null ? [] : [target.capability_id])),
    [orderedTargets],
  );

  /**
   * Binding a candidate whose four-tuple already carries an unpinned row of
   * this SKU is NOT an insert: `upsertSkuTarget` matches that row and moves it
   * onto the new capability, force-enabled, with a success toast. Route the
   * operator into the pin flow instead — same repair, stated honestly.
   */
  function handleBind(candidate: SkuSupplyCandidate): void {
    // The guard below is only as good as the target list it searches, and
    // `isSuccess` is the freshness test, not emptiness. A FIRST read that
    // fails leaves `orderedTargets` empty; a background refetch that fails
    // leaves React Query's last good payload in place, so the list is
    // non-empty but no longer confirmed. Either way a PUT onto a four-tuple
    // whose colliding row we cannot currently vouch for is the in-place UPDATE
    // this guard exists to prevent. An unverifiable write is refused, not
    // attempted.
    if (!targetsLoaded) {
      toast.error(t('sku_detail.supply.bind_blocked_targets_unknown'));
      return;
    }
    const matched = findRowMatchedByUpsert(orderedTargets, candidate);
    if (matched !== null) {
      if (matched.capability_id !== null) {
        // Not reachable through this button: `groupSupplyRows` withholds a
        // bind button from any candidate a bound row already matches, and the
        // server's own `bound` flag is a second floor. Kept as a fail-closed
        // arm rather than an assumption — if either of those ever slips, the
        // consequence is a refused click, not a relocated binding.
        toast.error(t('sku_detail.supply.write_hits_bound_row', {
          channel: matched.upstream_channel_slug,
        }));
        return;
      }
      toast.info(t('sku_detail.supply.bind_hits_legacy_row', {
        channel: matched.upstream_channel_slug,
      }));
      setPinSeedCapabilityId(candidate.capability_id);
      setPinTarget(matched);
      return;
    }
    writes.bindCandidate(candidate);
  }

  /**
   * The manual add-target dialog needs the same guard, and needs MORE of it
   * than `handleBind` does (#1431).
   *
   * Whether a PUT inserts or updates is the server's decision, not this
   * panel's: `upsertSkuTarget` looks the row up on the four-tuple with
   * `(capability_id = $n OR capability_id IS NULL)`.
   * `TenantRoutingTargetDialog` offers every capability under the chosen
   * four-tuple that this SKU's supply-candidate read calls bindable — it does
   * not exclude the ones this SKU's own targets already pin — so an operator
   * can hand-assemble a submission that lands on EITHER arm.
   *
   * The two paths are not equivalent, which is why the arms are not handled
   * the same way here as there. `groupSupplyRows` withholds a bind button from
   * any candidate a bound row already matches, so the pinned arm is
   * structurally unreachable from a bind and reachable only from this dialog.
   * And this path submits a `weight` (draft default `'100'`) where the bind
   * path deliberately omits one, so the matched row does not merely move — its
   * traffic share, retry order and enabled flag are overwritten from a draft
   * the operator believed was describing a NEW row.
   */
  async function handleAddTarget(submission: SkuTargetDialogSubmission): Promise<boolean> {
    if (!targetsLoaded) {
      toast.error(t('sku_detail.supply.bind_blocked_targets_unknown'));
      return false;
    }
    const matched = findRowMatchedByUpsert(orderedTargets, submission);
    if (matched !== null && matched.capability_id !== null) {
      // Nothing to repair: this exact binding already exists and is edited in
      // place in the bound group, which is where changing its weight belongs.
      // The dialog stays open so the operator can pick a different lane
      // instead of losing the draft to a refusal.
      toast.error(t('sku_detail.supply.write_hits_bound_row', {
        channel: matched.upstream_channel_slug,
      }));
      return false;
    }
    if (matched !== null) {
      // The capability the operator picked is the one they meant this lane to
      // serve, and the pin flow applies it without relocating the legacy row's
      // weight behind their back. Seeding it is safe for the same reason the
      // diverted bind's seed is: `PinCapabilityDialog` gates confirm on
      // membership of its own options, so a capability a sibling already pins
      // is dropped rather than offered.
      setPinSeedCapabilityId(submission.capability_id);
      setPinTarget(matched);
      // The add dialog closes even though nothing was written — two stacked
      // dialogs would leave the abandoned draft looking like the live one — so
      // the message has to say so, and has to say that the weight just typed
      // will NOT be applied: `pinCapability` submits the stored value on
      // purpose. Its own key, not the bind path's: the verb there is "binding
      // directly", which is not what just happened.
      toast.info(t('sku_detail.supply.add_hits_legacy_row', {
        channel: matched.upstream_channel_slug,
      }));
      setAddOpen(false);
      return false;
    }
    return writes.addTarget(submission);
  }

  const targetsError = !targetsQ.isError
    ? null
    : (
      <p className="m-0 px-4 py-3 text-dense text-bad" data-testid="sku-supply-targets-error">
        {t('sku_detail.supply.targets_load_failed')}
      </p>
    );

  const candidatesError = !candidatesQ.isError
    ? null
    // Since #1411 P4-05 the gateway resolves identity from the published
    // `pub_sku_catalog` projection, rewritten on every catalog mutation — so
    // `CHANNEL_MODEL_NOT_FOUND` means the SKU is genuinely absent from the
    // published catalog, or a real integrity mismatch (namespace slug renamed
    // out from under the sku_name). The code is matched alongside the status
    // because a bare 404 (an unmounted proxy route, say) carries no such
    // meaning.
    : candidatesQ.error instanceof OpsApiError &&
      candidatesQ.error.status === 404 &&
      candidatesQ.error.code === 'CHANNEL_MODEL_NOT_FOUND'
      ? (
        <p className="m-0 px-4 py-3 text-dense leading-5 text-warn" data-testid="sku-supply-not-synced">
          {t('sku_detail.supply.not_synced')}
        </p>
      )
      : (
        <p className="m-0 px-4 py-3 text-dense text-bad" data-testid="sku-supply-error">
          {coerceErrorMessage(candidatesQ.error)}
        </p>
      );

  return (
    <Panel
      caption={
        <span className="inline-flex items-center gap-2">
          <span>{t('sku_detail.supply.title')}</span>
          <InfoHint
            label={t('sku_detail.hint.label')}
            content={t('sku_detail.hint.sku')}
            testId="sku-supply-hint-sku"
          />
          {emptyPool ? (
            <Chip tone="warn" lamp={false} testId="sku-pool-empty-chip">
              {t('tenant_routing.sku_pools.empty_pool')}
            </Chip>
          ) : null}
        </span>
      }
      actions={
        <>
          <Link
            to="/supply/tenant-routing"
            className="inline-flex items-center gap-1 text-label text-paper-low transition-colors duration-120 hover:text-paper"
            data-testid="sku-pool-open-tenant-routing"
          >
            {t('sku_detail.pool.open_tenant_routing')}
            <ExternalLink aria-hidden="true" className="h-3 w-3" />
          </Link>
          {/*
            Unavailable while the target list is unverifiable. The dialog's
            only outcome is a PUT whose insert-or-update fate depends on rows
            this panel cannot currently vouch for, so offering the form would
            be offering a write that `handleAddTarget` is then obliged to
            refuse — after the operator filled it in.

            The reason hangs on the WRAPPER, not the button: `Button`'s base
            class carries `disabled:pointer-events-none`, so a disabled button
            receives no mouseover and its own `title` would never render. The
            span still does.
          */}
          {/*
            `inline-flex`, not a bare span: as a flex item a bare span
            blockifies and still lays its inline-level button on a text
            baseline, leaving descender space that pushes the button ~2px off
            the row it shares with the sibling link. A flex container has no
            strut.
          */}
          <span
            className="inline-flex"
            title={targetsLoaded ? undefined : t('sku_detail.supply.bind_blocked_targets_unknown')}
          >
            <Button
              size="sm"
              disabled={!targetsLoaded}
              onClick={() => setAddOpen(true)}
              data-testid="sku-pool-add-target"
            >
              <Plus aria-hidden="true" className="h-3.5 w-3.5" />
              {t('tenant_routing.pool.add_target')}
            </Button>
          </span>
        </>
      }
      testId="sku-supply-panel"
    >
      <ExclusiveCoverageAlert sku={sku} impacts={impacts} copySource={exclusiveCopySource} />
      <p className="m-0 border-b border-line-soft px-4 py-2.5 text-dense leading-5 text-paper-low">
        {t('sku_detail.supply.hint')}
      </p>
      {targetsError}
      {candidatesError}
      {targetsQ.isPending || candidatesQ.isPending ? (
        <div className="flex flex-col gap-2 p-4">
          {[1, 2].map((i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : (
        <Table
          /*
            The wrapper already scrolls; without a floor the table just
            compresses instead, which is how the number inputs vanished at
            1440. Seven columns fit inside the app shell down to ~1280 — below
            that the operator scrolls rather than reads squeezed boxes.
          */
          className="rounded-none border-0 bg-transparent [&_table]:min-w-[960px]"
        >
          <SupplyTableHead />
          <TBody>
            <GroupHeaderRow testId="sku-supply-group-bound">
              <span className="inline-flex items-center gap-2 text-label text-paper">
                {t('sku_detail.supply.group.bound', { count: String(groups.bound.length) })}
              </span>
            </GroupHeaderRow>
            {groups.bound.length === 0 ? (
              /*
                Only when the pool read actually succeeded. "Nothing is bound"
                is a claim about `routing_default_sku_targets`; derived from a
                failed read it is a confident lie about a SKU that may well be
                serving traffic — same error `issueFor` refuses to make on the
                candidate side. The error surface above says what went wrong.
              */
              targetsLoaded ? (
                <GroupNoticeRow testId="sku-supply-bound-empty">
                  {/*
                    "Bind one of the candidates below" is only advice while
                    there ARE candidates below. With both groups empty the
                    panel used to say pick one from a list and then, one row
                    later, that the list is empty. One sentence per situation.
                  */}
                  {t(nothingAvailable
                    ? 'sku_detail.supply.group.nothing_at_all'
                    : 'sku_detail.supply.group.bound_empty')}
                </GroupNoticeRow>
              ) : null
            ) : (
              groups.bound.map((row) => (
                <BoundSupplyRow
                  key={row.target.id}
                  row={row}
                  saving={writes.saving}
                  onSave={writes.saveBound}
                  onDelete={setDeleteRow}
                  onPin={(target) => {
                    setPinSeedCapabilityId(null);
                    setPinTarget(target);
                  }}
                />
              ))
            )}

            {/*
              With no candidate answer there is nothing to recommend AND
              nothing to rule out, so both candidate groups stay off screen.
              Rendering "Recommended (0)" would assert that no same-model
              supply exists — a claim derived from a list never received. The
              error notice above already says what actually went wrong.
            */}
            {candidatesLoaded ? (
              <>
                <GroupHeaderRow testId="sku-supply-group-recommended">
                  <span className="inline-flex items-center gap-2 text-label text-paper">
                    {t('sku_detail.supply.group.recommended', {
                      count: String(groups.recommended.length),
                    })}
                  </span>
                </GroupHeaderRow>
                {groups.recommended.length === 0 ? (
                  // Suppressed when the bound group already said everything is
                  // empty — see `nothingAvailable`.
                  nothingAvailable ? null : (
                    <GroupNoticeRow testId="sku-supply-recommended-empty">
                      {t('sku_detail.supply.group.recommended_empty')}
                    </GroupNoticeRow>
                  )
                ) : (
                  groups.recommended.map((candidate) => (
                    <CandidateSupplyRow
                      key={candidate.capability_id}
                      candidate={candidate}
                      onBind={handleBind}
                      binding={writes.saving}
                    />
                  ))
                )}
              </>
            ) : null}

            {candidatesLoaded && groups.crossModel.length > 0 ? (
              <>
                <GroupHeaderRow testId="sku-supply-group-cross-model">
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 text-label text-paper transition-colors duration-120 hover:text-signal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
                    aria-expanded={crossModelOpen}
                    onClick={() => setCrossModelOpen((open) => !open)}
                    data-testid="sku-supply-cross-model-toggle"
                  >
                    {crossModelOpen
                      ? <ChevronDown aria-hidden="true" className="h-3.5 w-3.5" />
                      : <ChevronRight aria-hidden="true" className="h-3.5 w-3.5" />}
                    {t('sku_detail.supply.group.cross_model', {
                      count: String(groups.crossModel.length),
                    })}
                  </button>
                </GroupHeaderRow>
                {crossModelOpen
                  ? groups.crossModel.map((candidate) => (
                    <CandidateSupplyRow
                      key={candidate.capability_id}
                      candidate={candidate}
                      onBind={handleBind}
                      binding={writes.saving}
                    />
                  ))
                  : null}
              </>
            ) : null}
          </TBody>
        </Table>
      )}

      <SupplyPolicyFooter policy={writes.policy} />

      {addOpen ? (
        <TenantRoutingTargetDialog
          open
          sku={sku}
          namespaceChannel={namespaceChannel}
          namespaceSkuCount={1}
          target={null}
          saving={writes.saving}
          onClose={() => setAddOpen(false)}
          onSubmit={(submission) => {
            void handleAddTarget(submission).then((done) => {
              if (done) setAddOpen(false);
            });
          }}
        />
      ) : null}

      {pinTarget !== null ? (
        <PinCapabilityDialog
          target={pinTarget}
          skuName={sku.skuName}
          capabilityIdsInUse={capabilityIdsInUse}
          initialCapabilityId={pinSeedCapabilityId}
          saving={writes.saving}
          onClose={() => setPinTarget(null)}
          onConfirm={(capabilityId) => {
            void writes.pinCapability(pinTarget, capabilityId).then((done) => {
              if (done) setPinTarget(null);
            });
          }}
        />
      ) : null}

      {deleteRow !== null ? (
        <ConfirmDialog
          open
          onOpenChange={(next) => {
            if (!next) setDeleteRow(null);
          }}
          tone="danger"
          title={t('sku_detail.supply.delete_confirm_title')}
          body={
            /*
              Labelled, not three bare values joined by slashes: an upstream
              named the same as its channel slug (the common case on dev) read
              as the same word printed twice, so the sentence carried no
              information about WHICH lane was about to go.
            */
            <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1" data-testid="sku-supply-delete-identity">
              <dt className="text-label text-paper-low">{t('sku_detail.supply.column.upstream')}</dt>
              <dd className="m-0 font-mono text-dense text-paper">
                {deleteRow.candidate?.upstream_name ?? deleteRow.target.upstream_id}
              </dd>
              <dt className="text-label text-paper-low">{t('sku_detail.supply.column.upstream_channel')}</dt>
              <dd className="m-0 font-mono text-dense text-paper">
                {deleteRow.target.upstream_channel_slug}
              </dd>
              <dt className="text-label text-paper-low">{t('sku_detail.supply.column.upstream_model')}</dt>
              <dd className="m-0 font-mono text-dense text-paper">
                {deleteRow.candidate?.model
                  ?? deleteRow.target.upstream_model_name
                  ?? deleteRow.target.model}
              </dd>
              <dd className="col-span-2 m-0 mt-1 text-dense text-paper-mid">
                {t('sku_detail.supply.delete_confirm_body')}
              </dd>
            </dl>
          }
          confirmLabel={t('sku_detail.supply.delete_confirm')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          loading={writes.deleting}
          onConfirm={() => {
            void writes.deleteTarget(deleteRow.target).then((done) => {
              if (done) setDeleteRow(null);
            });
          }}
          testId="sku-supply-delete-confirm"
        />
      ) : null}
    </Panel>
  );
}
